/**
 * The shell's shared state, in a file of its own so both the Sidebar and the
 * Topbar can reach it without either importing the other, and so `AppShell.tsx`
 * exports nothing but a component.
 */

import { createContext, useContext } from 'react'
import type { Density, Scheme } from './preferences'

export interface ShellState {
  /** Icons only, labels clipped. Never true while `drawer` is true. */
  collapsed: boolean
  setCollapsed(next: boolean): void
  /** The viewport is narrow enough that the sidebar is a drawer, not a column. */
  drawer: boolean
  drawerOpen: boolean
  setDrawerOpen(next: boolean): void
  scheme: Scheme
  setScheme(next: Scheme): void
  density: Density
  setDensity(next: Density): void
}

export const ShellContext = createContext<ShellState | null>(null)

/**
 * Throws outside an `AppShell` rather than returning a default.
 *
 * A default would let a Topbar rendered on its own look entirely correct while
 * its scheme control wrote to nothing and its menu button opened nothing. That
 * failure is invisible in a screenshot and invisible in a test that only checks
 * the button is there, and it surfaces as "the dark mode toggle does nothing"
 * weeks later.
 */
export function useShell(): ShellState {
  const state = useContext(ShellContext)
  if (!state) {
    throw new Error('useShell requires an <AppShell> above it in the tree.')
  }
  return state
}
